import { DesignScheme } from '@data/buildingProjects/DesignSchemes/designScheme';
import { DesignSchemeSet } from '@data/buildingProjects/DesignSchemeSet';
import FVector from '@data/geometry/FVector';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { useDesignSchemesBucket } from '@gui/store/persistent/persistentStore';
import { Box, Divider } from '@mui/material';
import { DrawModule } from '@rendering/DrawingModule/DrawingModule';
import { PolygonDrawerManager } from '@rendering/DrawingModule/PolygonDrawerManager';
import { ContextState } from '@visualizer/types';
import React from 'react';
import Grid from '../../../Geodata/grid';
import { PolygonDrawer } from '../DrawModule/PolygonDrawer';
import { designSchemeUIRegistry } from './DesignSchemeUIRegistry';

export interface FootprintActions {
    toggleEdit(): void;
    delete(): void;
    isEditing(): boolean;
}

type FootprintProps = {
    footprint: { id: string; points: FVector[] };
    idx: number;
    selectedProject: DesignSchemeSet;
    polygonDrawerRefs: Record<string, DrawModule>;
    context: ContextState;
    setContextPanel: (updates: Partial<ContextState>) => void;
    onEditEnd: () => void;
    onEditStart: () => void;
};

const FootprintView = React.forwardRef<FootprintActions, FootprintProps>(
    ({ footprint, idx, selectedProject, polygonDrawerRefs, context, setContextPanel, onEditEnd, onEditStart }, ref) => {
        // Controllers:
        const designSchemeController = ControllerManager.getInstance().getDesignSchemeController();
        const pipelineController = ControllerManager.getInstance().getPipelineController();

        // React State:
        const [activeFootprintIdx, setActiveFootprintIdx] = React.useState<number | null>(null);
        const [restoreSiteGrid, setRestoreSiteGrid] = React.useState(false);

        // Helpers:
        const rememberGridState = () => {
            setRestoreSiteGrid(context.showGrid);
        };

        const restoreGridIfNeeded = () => {
            if (!restoreSiteGrid) return;
            setContextPanel({ showGrid: true });
            setRestoreSiteGrid(false);
        };

        // Helper: persist polygon change to Zustand + update runtime schemes
        const createOnSaveHandler = (footprint: { id: string; points: FVector[] }) => {
            const setId = selectedProject.getID();
            const fpId = footprint.id;

            return (newPts: FVector[], failed: boolean) => {
                if (!failed) {
                    const newVectorArray = newPts.map((v) => new FVector(v.x, v.y, v.z));

                    // 1) Update runtime footprint reference first so any downstream post-block sync sees the new geometry.
                    footprint.points = newVectorArray;

                    // 2) Notify all design schemes bound to this footprint
                    const designSchemes = designSchemeController.getDesignSchemesByFootprint(selectedProject.getID(), fpId);
                    designSchemes?.forEach((scheme) => scheme.updateFootprint(newVectorArray));

                    // 3) Persist polygon in Zustand and trigger post-block refresh against the already-updated runtime geometry.
                    designSchemeController.updateFootprint(setId, fpId, { polygon: newVectorArray });

                    // 4) Re-run the active pipeline so DS post blocks reflect the new footprint immediately.
                    pipelineController.run();

                    setActiveFootprintIdx(null);
                }

                // 5) Close editor & restore grid
                restoreGridIfNeeded();

                // Bonus) Generate closed polyline as we're fully owning this Draw Module and we need therefore to handle the failing.
                if (failed) {
                    const module = polygonDrawerRefs[footprint.id];
                    module.visualEffects.removeSpheres();
                    module.visualEffects.removePolyLine();
                    const closedPoints = [...footprint.points, footprint.points[0]];
                    module.visualEffects.generatePolyLine(closedPoints);
                }
            };
        };

        const ensureDrawModule = React.useCallback(() => {
            if (!polygonDrawerRefs[footprint.id]) {
                polygonDrawerRefs[footprint.id] = new DrawModule(createOnSaveHandler(footprint), Grid.getPoints());
                onEditStart();
            }
        }, [footprint.id]);

        // Get Design Schemes that belong to this footprint:
        const setId = selectedProject.getID();

        // Show only the active schemes in the UI:
        const footprintSchemeState = useDesignSchemesBucket((s) => s.sets?.[setId]?.footprints?.[footprint.id]?.designSchemes);
        const visibleSchemeIds = React.useMemo(() => {
            if (!footprintSchemeState) return new Set<string>();
            return new Set(
                Object.entries(footprintSchemeState)
                    .filter(([, state]) => state?.visibility === true)
                    .map(([id]) => id)
            );
        }, [footprintSchemeState]);

        const designSchemes = designSchemeController
            .getDesignSchemes(setId)
            ?.filter((ds) => ds.getFootprintId?.() === footprint.id && visibleSchemeIds.has(ds.getID()));

        /* Imperative API for DesignSchemeSet */
        React.useImperativeHandle(ref, () => ({
            toggleEdit() {
                ensureDrawModule();
                const drawer = polygonDrawerRefs[footprint.id];
                const manager = PolygonDrawerManager.getInstance();

                // Close editor if already active:
                if (activeFootprintIdx === idx) {
                    setActiveFootprintIdx(null);
                    restoreGridIfNeeded();
                    drawer.stop();
                    return;
                }

                // Request activation via the manager:
                manager.requestActivation(drawer).then((ok) => {
                    if (!ok) return;

                    rememberGridState();
                    if (context.showGrid) setContextPanel({ showGrid: false });

                    setActiveFootprintIdx(idx);

                    drawer.start();
                });
            },

            delete() {
                const fpId = footprint.id;
                const drawModule = polygonDrawerRefs[fpId];
                if (drawModule) {
                    PolygonDrawerManager.getInstance().unregister(drawModule, true);
                    delete polygonDrawerRefs[fpId];
                }
                designSchemeController.removeFootprint(selectedProject.getID(), fpId);
            },

            isEditing() {
                return activeFootprintIdx === idx;
            },
        }));

        // Restores grid if ncessary & handles edit button state:
        React.useEffect(() => {
            let drawer = polygonDrawerRefs[footprint.id];
            if (!drawer) ensureDrawModule();
            drawer = polygonDrawerRefs[footprint.id];

            const onStopped = () => {
                restoreGridIfNeeded();
                setActiveFootprintIdx(null);
                onEditEnd(); // ONLY HERE
            };

            const onStarted = () => {
                onEditStart(); // ONLY HERE
            };

            drawer.on?.('stopped', onStopped);
            drawer.on?.('started', onStarted);

            return () => {
                drawer.off?.('stopped', onStopped);
                drawer.off?.('started', onStarted);
            };
        }, [footprint.id, restoreSiteGrid, polygonDrawerRefs]);

        return (
            <React.Fragment key={idx}>
                {/* ─── Polygon Drawer ───────────────────────────── */}
                {activeFootprintIdx === idx && (
                    <>
                        {/* {ensureDrawModule()} */}
                        <PolygonDrawer
                            isActive={activeFootprintIdx === idx}
                            initialPoints={footprint.points}
                            drawModule={polygonDrawerRefs[footprint.id]}
                        />
                    </>
                )}

                {/* ─── Design Schemes Section ──────────────────── */}
                {footprint.points.length > 2 && (
                    <>
                        {designSchemes?.map((scheme) => {
                            const SchemeClass = scheme.constructor as new (...args: any[]) => DesignScheme;
                            const GuiComponent = designSchemeUIRegistry.get(SchemeClass);

                            return (
                                <React.Fragment key={scheme.getID()}>
                                    <Divider sx={{ my: 0.5 }} />

                                    <Box>
                                        {GuiComponent && (
                                            <GuiComponent
                                                setId={scheme.getDesignSchemeSet().getID()}
                                                footprintId={scheme.getFootprintId()}
                                                schemeId={scheme.getID()}
                                            />
                                        )}
                                    </Box>
                                </React.Fragment>
                            );
                        })}
                    </>
                )}

                {/* Divider between footprints */}
                <Divider sx={{ my: 2 }} />
            </React.Fragment>
        );
    }
);

export default FootprintView;
